import type {
  EmployerFootprintAdminViewDto,
  EmployerVerificationDetail,
} from '../../api/employerVerificationDashboardApi';
import type { ComparedValueRow } from '../checkpointComparedValues';
import { comparedValuesForCheckpointStep } from '../checkpointComparedValues';
import { buildSectorMismatchIssueStatement, readObservedEmployerSector } from './employerObservedSector';
import { buildAiRoleSectorAdjudicatorView } from './parseAiRoleSectorAnalysis';
import { readRoleMismatchContext } from './roleMismatchFindingPresentation';
import type { ReviewReasonEvidence } from './reviewReason.types';

export type StructuralSanityCheckpointView = {
  popoverTitle: string;
  dataPoints: ReviewReasonEvidence[];
  issueStatement: string;
  issueSeverity: 'warning' | 'error' | 'info';
  issueLabel: string;
  footerNote: string | null;
  advisoryLabel: string | null;
  advisorySeverity: 'warning' | 'success' | 'default';
};

export const STRUCTURAL_SANITY_CHECKPOINT_STEPS = [
  'ONSITE_ROLE_VS_REMOTE',
  'SECTOR_ROLE_MATCH',
  'DECLARED_SECTOR_VS_EMPLOYER_NAICS',
  'INFRA_MATCH',
  'SECTOR_INFRA_MISMATCH',
  'EMPLOYMENT_CONTEXT_CONTRADICTION',
  'COMMUTE_DISTANCE',
  'CROSS_PROVINCE_COMMUTE',
  'AI_ROLE_SECTOR_ANALYSIS',
] as const;

export type StructuralSanityCheckpointStep = (typeof STRUCTURAL_SANITY_CHECKPOINT_STEPS)[number];

const STEP_POPOVER_TITLES: Record<StructuralSanityCheckpointStep, string> = {
  ONSITE_ROLE_VS_REMOTE: 'Working type vs role',
  SECTOR_ROLE_MATCH: 'Sector & role alignment',
  DECLARED_SECTOR_VS_EMPLOYER_NAICS: 'Declared sector vs employer NAICS',
  INFRA_MATCH: 'Role vs workplace footprint',
  SECTOR_INFRA_MISMATCH: 'Sector vs workplace footprint',
  EMPLOYMENT_CONTEXT_CONTRADICTION: 'Employment context check',
  COMMUTE_DISTANCE: 'Commute distance',
  CROSS_PROVINCE_COMMUTE: 'Cross-province commute',
  AI_ROLE_SECTOR_ANALYSIS: 'AI role & sector pre-read',
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function asStringList(v: unknown): string | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  const parts = v
    .map((x) => (typeof x === 'string' ? x.trim() : String(x ?? '').trim()))
    .filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

function structuralEvidence(evidence: Record<string, unknown> | null): Record<string, unknown> {
  if (!evidence) return {};
  const nested = evidence.gateEvidence;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const structural = (nested as Record<string, unknown>).STRUCTURAL_SANITY;
    if (structural && typeof structural === 'object' && !Array.isArray(structural)) {
      return { ...evidence, ...(structural as Record<string, unknown>) };
    }
  }
  const gateEv = evidence.STRUCTURAL_SANITY;
  if (gateEv && typeof gateEv === 'object' && !Array.isArray(gateEv)) {
    return { ...evidence, ...(gateEv as Record<string, unknown>) };
  }
  return evidence;
}

function pushPoint(
  rows: ReviewReasonEvidence[],
  label: string,
  value: string | null | undefined,
  tone: ReviewReasonEvidence['tone'],
) {
  if (value) rows.push({ label, value, tone });
}

function normalizeStep(step: string): StructuralSanityCheckpointStep | null {
  const u = step.trim().toUpperCase();
  return (STRUCTURAL_SANITY_CHECKPOINT_STEPS as readonly string[]).includes(u)
    ? (u as StructuralSanityCheckpointStep)
    : null;
}

export function isStructuralSanityCheckpoint(step: string): boolean {
  return normalizeStep(step) != null;
}

function stripTechnicalTokens(text: string): string {
  return text
    .replace(/\b(?:SECTOR_ROLE_MATCH|INFRA_MATCH|EMPLOYMENT_CONTEXT_CONTRADICTION|AI_ROLE_SECTOR_ANALYSIS)\b/gi, '')
    .replace(/\s+are\s+REVIEW\.?/gi, '.')
    .replace(/\s+is\s+incorrectly\s+PASS\.?/gi, '.')
    .replace(/\s{2,}/g, ' ')
    .replace(/\.\s*\./g, '.')
    .trim();
}

function oneSentence(text: string, maxLen = 240): string {
  const cleaned = stripTechnicalTokens(text);
  if (!cleaned) return '';
  if (cleaned.length <= maxLen) return cleaned.endsWith('.') ? cleaned : `${cleaned}.`;
  return `${cleaned.slice(0, Math.max(0, maxLen - 1)).trim()}…`;
}

function bestIssueSentence(text: string): string {
  const cleaned = stripTechnicalTokens(text);
  if (!cleaned) return '';
  const sentences = cleaned.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean);
  const attention = sentences.find((s) =>
    /does not|exceeds|could not|marked for review|misrepresentation|contradict|unrealistic|mismatch|inconsistent|attention:|not align|not fit|not strong enough|skipped|within the acceptable/i.test(
      s,
    ),
  );
  if (attention) return oneSentence(attention);
  if (sentences.length > 1) return oneSentence(sentences[sentences.length - 1]!);
  return oneSentence(cleaned);
}

function issueMetaFromBucket(bucket: string): {
  severity: StructuralSanityCheckpointView['issueSeverity'];
  label: string;
} {
  const u = bucket.trim().toLowerCase();
  if (u === 'fail') return { severity: 'error', label: 'Issue' };
  if (u === 'pass') return { severity: 'info', label: 'Outcome' };
  return { severity: 'warning', label: 'Issue' };
}

function comparedMap(compared: ComparedValueRow[]): Map<string, string> {
  return new Map(compared.map((row) => [row.label, row.value]));
}

function parseProvincesFromReason(reason: string): { applicant: string | null; employer: string | null } {
  const match = reason.match(/Applicant\s*\(([^)]+)\)\s*and\s*employer\s*\(([^)]+)\)/i);
  if (match?.[1] && match?.[2]) {
    return { applicant: match[1].trim(), employer: match[2].trim() };
  }
  const arrow = reason.match(/Inter-provincial daily commute\s*\(([^→]+)→([^)]+)\)/i);
  if (arrow?.[1] && arrow?.[2]) {
    return { applicant: arrow[1].trim(), employer: arrow[2].trim() };
  }
  return { applicant: null, employer: null };
}

function buildDataPointsForStep(
  step: StructuralSanityCheckpointStep,
  input: {
    detail: EmployerVerificationDetail;
    footprint?: EmployerFootprintAdminViewDto | null;
    compared: ComparedValueRow[];
    reason: string;
  },
): ReviewReasonEvidence[] {
  const evidence = asRecord(input.detail.evidence);
  const struct = structuralEvidence(evidence);
  const compared = comparedMap(input.compared);
  const roleCtx = readRoleMismatchContext({
    detail: input.detail,
    footprint: input.footprint,
  });
  const rows: ReviewReasonEvidence[] = [];

  switch (step) {
    case 'ONSITE_ROLE_VS_REMOTE':
      pushPoint(rows, 'Role', compared.get('Job title') ?? roleCtx.jobTitle, 'role');
      pushPoint(rows, 'Working type', compared.get('Working type'), 'declared');
      pushPoint(rows, 'Remote policy', compared.get('Remote policy'), 'policy');
      break;

    case 'SECTOR_ROLE_MATCH':
      pushPoint(rows, 'Customer declared', roleCtx.declaredIndustry, 'declared');
      pushPoint(
        rows,
        'Employer sector (observed)',
        readObservedEmployerSector({
          detail: input.detail,
          footprint: input.footprint,
          checkpointReason: input.reason,
        }),
        'observed',
      );
      pushPoint(rows, 'Employer', roleCtx.employerName, 'employer');
      pushPoint(rows, 'Role', roleCtx.jobTitle, 'role');
      break;

    case 'DECLARED_SECTOR_VS_EMPLOYER_NAICS':
      pushPoint(rows, 'Customer declared', compared.get('Declared sector') ?? roleCtx.declaredIndustry, 'declared');
      pushPoint(rows, 'Employer NAICS', compared.get('Employer NAICS'), 'employer');
      pushPoint(rows, 'Expected NAICS', compared.get('Expected NAICS'), 'observed');
      pushPoint(rows, 'Alignment', compared.get('Alignment'), 'policy');
      break;

    case 'INFRA_MATCH':
    case 'SECTOR_INFRA_MISMATCH':
      pushPoint(rows, 'Role', compared.get('Job title') ?? roleCtx.jobTitle, 'role');
      pushPoint(rows, 'Expected workplace', compared.get('Expected workplace'), 'declared');
      pushPoint(rows, 'Observed workplace', compared.get('Observed workplace'), 'workplace');
      pushPoint(
        rows,
        'Primary business',
        compared.get('Primary business type') ?? readString(struct.primaryBusinessType),
        'observed',
      );
      break;

    case 'EMPLOYMENT_CONTEXT_CONTRADICTION': {
      const ctxAssessment = asRecord(struct.employmentContextAssessment);
      const signals = asRecord(ctxAssessment?.signals);
      pushPoint(rows, 'Customer declared', roleCtx.declaredIndustry, 'declared');
      pushPoint(
        rows,
        'Workplace signals',
        compared.get('Workplace signals') ?? asStringList(struct.placeTypes),
        'workplace',
      );
      pushPoint(
        rows,
        'Occupation match',
        compared.get('Occupation match') ?? readString(signals?.occupationCategoryMatch),
        'observed',
      );
      pushPoint(rows, 'Role', roleCtx.jobTitle, 'role');
      break;
    }

    case 'COMMUTE_DISTANCE':
      pushPoint(rows, 'Commute distance', compared.get('Commute distance'), 'observed');
      pushPoint(rows, 'Working type', compared.get('Working type'), 'declared');
      pushPoint(rows, 'Policy limit', '150 km', 'policy');
      pushPoint(rows, 'Employer location', compared.get('Employer address'), 'employer');
      break;

    case 'CROSS_PROVINCE_COMMUTE': {
      const provinces = parseProvincesFromReason(input.reason);
      pushPoint(rows, 'Working type', compared.get('Working type'), 'declared');
      pushPoint(rows, 'Applicant province', compared.get('Applicant province') ?? provinces.applicant, 'observed');
      pushPoint(rows, 'Employer province', compared.get('Employer province') ?? provinces.employer, 'employer');
      pushPoint(rows, 'Commute scope', 'Same province for onsite', 'policy');
      break;
    }

    default:
      break;
  }

  if (rows.length === 0) {
    return input.compared.slice(0, 4).map((row, index) => ({
      label: row.label,
      value: row.value,
      tone: (['declared', 'observed', 'employer', 'role'] as const)[index % 4],
    }));
  }

  return rows.slice(0, 4);
}

function buildIssueStatement(
  step: StructuralSanityCheckpointStep,
  reason: string,
  bucket: string,
  input: {
    detail: EmployerVerificationDetail;
    footprint?: EmployerFootprintAdminViewDto | null;
  },
): string {
  const roleCtx = readRoleMismatchContext({
    detail: input.detail,
    footprint: input.footprint,
  });

  if (step === 'SECTOR_ROLE_MATCH' || step === 'SECTOR_INFRA_MISMATCH') {
    const observed = readObservedEmployerSector({
      detail: input.detail,
      footprint: input.footprint,
      checkpointReason: reason,
    });
    const mismatch = buildSectorMismatchIssueStatement({
      declaredIndustry: roleCtx.declaredIndustry,
      observedSector: observed,
      employerName: roleCtx.employerName,
    });
    if (bucket !== 'pass' && mismatch) return mismatch;
  }

  if (step === 'AI_ROLE_SECTOR_ANALYSIS') {
    return buildAiRoleSectorAdjudicatorView({
      reason,
      detail: input.detail,
      footprint: input.footprint,
    }).issueStatement;
  }

  const sentence = bestIssueSentence(reason);
  if (sentence) return sentence;

  if (bucket === 'pass') return 'This check passed based on the available evidence.';
  return 'Review the compared values and confirm they match the application.';
}

/** OPG-style adjudicator panel for each structural sanity checkpoint popover. */
export function buildStructuralSanityCheckpointView(input: {
  step: string;
  reason: string;
  bucket: string;
  detail: EmployerVerificationDetail;
  footprint?: EmployerFootprintAdminViewDto | null;
}): StructuralSanityCheckpointView | null {
  const step = normalizeStep(input.step);
  if (!step) return null;

  if (step === 'AI_ROLE_SECTOR_ANALYSIS') {
    const ai = buildAiRoleSectorAdjudicatorView({
      reason: input.reason,
      detail: input.detail,
      footprint: input.footprint,
    });
    const meta = issueMetaFromBucket(input.bucket);
    return {
      popoverTitle: STEP_POPOVER_TITLES[step],
      dataPoints: ai.dataPoints,
      issueStatement: ai.issueStatement,
      issueSeverity: meta.severity,
      issueLabel: meta.label,
      footerNote: ai.footerNote,
      advisoryLabel: ai.advisoryLabel,
      advisorySeverity: ai.advisorySeverity,
    };
  }

  const compared =
    input.detail.evidence != null
      ? comparedValuesForCheckpointStep(step, input.detail.evidence as Record<string, unknown>)
      : [];

  const dataPoints = buildDataPointsForStep(step, {
    detail: input.detail,
    footprint: input.footprint,
    compared,
    reason: input.reason,
  });

  const meta = issueMetaFromBucket(input.bucket);

  return {
    popoverTitle: STEP_POPOVER_TITLES[step],
    dataPoints,
    issueStatement: buildIssueStatement(step, input.reason, input.bucket, {
      detail: input.detail,
      footprint: input.footprint,
    }),
    issueSeverity: meta.severity,
    issueLabel: meta.label,
    footerNote: null,
    advisoryLabel: null,
    advisorySeverity: 'default',
  };
}
